import { FunctionComponent, useEffect, useRef, useState } from "react";
import SignaturePad from "signature_pad";
import { ComponentId } from "../../../lib/types/ids";
import Button from "../Button/Button";
import { UploadedFile } from "../form-data";

export interface SignFieldProps {
  id: ComponentId;
  label: string;
  showLabel: boolean;
  setFile: (file: UploadedFile | undefined, id: ComponentId) => void;
  error?: boolean;
  errorText?: string;
  isActive: boolean;
}

interface ResizeValues {
  scale: number;
  width: number;
  height: number;
}

const SignField: FunctionComponent<SignFieldProps> = ({
  id,
  label,
  showLabel,
  setFile,
  error,
  errorText,
  isActive,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const [signaturePad, setSignaturePad] = useState<SignaturePad>();
  const [lastResizeValues, setLastResizeValues] = useState<ResizeValues>();
  const [dataURL, setDataURL] = useState<string>();
  const [canvasChanged, setCanvasChanged] = useState<boolean>();

  useEffect(() => {
    const canvasContainer = canvasContainerRef.current;
    const canvas = canvasRef.current;
    if (canvas === null || canvasContainer === null || !isActive) {
      setSignaturePad(undefined);
      return;
    }

    const signaturePad = new SignaturePad(canvas);
    signaturePad.on();

    setSignaturePad(signaturePad);

    const canvasChecked = canvas;
    const canvasContainerChecked = canvasContainer;
    function resizeCanvas() {
      const canvas = canvasChecked;
      const canvasContainer = canvasContainerChecked;
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      const startingWidth = canvasContainer.offsetWidth;
      const startingHeight = canvasContainer.offsetHeight;
      if (startingWidth === 0 || startingHeight === 0) {
        requestAnimationFrame(resizeCanvas);
        return;
      }
      const newWidth = Math.round(startingWidth * ratio);
      const newHeight = Math.round(startingHeight * ratio);
      if (lastResizeValues
        && lastResizeValues.scale === ratio
        && lastResizeValues.width === newWidth
        && lastResizeValues.height === newHeight)
        return;

      canvas.width = newWidth;
      canvas.height = newHeight;
      canvas.getContext("2d")?.scale(ratio, ratio);
      signaturePad.clear(); // otherwise isEmpty() might return incorrect value
      const newLastResizeValues = { scale: ratio, width: newWidth, height: newHeight };
      setLastResizeValues(newLastResizeValues);
      console.log(newLastResizeValues);
    }

    window.addEventListener("resize", resizeCanvas);
    resizeCanvas();

    return () => {
      window.removeEventListener("resize", resizeCanvas);
      signaturePad.off();
      setSignaturePad(undefined);
    };
  }, [isActive, lastResizeValues]);

  useEffect(() => {
    if (!signaturePad)
      return;

    const onEndStroke = () => {
      setCanvasChanged(true);
      // Im geänderten Zustand brauchen wir eine Entscheidung über die Buttons.
      // Damit wir leicht die isRequired-Validierung umsetzen können, setzen wir
      // nach einer Änderung am Canvas einfach den Wert zurück.
      setFile(undefined, id);
    };

    signaturePad.addEventListener("endStroke", onEndStroke);

    return () => {
      signaturePad.removeEventListener("endStroke", onEndStroke);
    };
  }, [signaturePad, setFile, id]);

  const onApply = () => {
    if (!signaturePad)
      return;

    if (signaturePad.isEmpty()) {
      setFile(undefined, id);
      setDataURL(undefined);
    } else {
      const imageUrl = signaturePad.toDataURL();
      setDataURL(imageUrl);
      const base64 = imageUrl.split(',')[1];
      const image = base64 ? Buffer.from(base64, "base64") : undefined;
      setFile(image ? { buffer: image, type: "image/png" } : undefined, id);
    }
    setCanvasChanged(false);
  };

  const onDiscard = () => {
    if (!signaturePad)
      return;

    signaturePad.clear();
    if (canvasChanged) {
      if (dataURL) {
        signaturePad.fromDataURL(dataURL);
        const base64 = dataURL.split(',')[1];
        const image = base64 ? Buffer.from(base64, "base64") : undefined;
        setFile(image ? { buffer: image, type: "image/png" } : undefined, id);
      } else {
        setFile(undefined, id);
      }
      setCanvasChanged(false);
    } else if (dataURL) {
      setDataURL(undefined);
      setFile(undefined, id);
    }
  };

  return (
    <div className="flex flex-col items-center -px-4 mb-8 component signature" data-id={ComponentId.encode(id)}>
      <div className="flex flex-col">
        <div className="flex flex-row w-full items-center">
          <div className={`flex flex-1 text-sm mb-2 ${!label || !showLabel ? "hidden" : ""}`}>
            <label
              id={[id, "label"].join("-")}
              htmlFor={[id, "canvas"].join("-")}
            >
              {label}
            </label>
          </div>
          <Button
            className="flex ml-1 mb-1 shrink-0"
            variant={signaturePad?.isEmpty() ? "outline" : "primary"}
            isDisabled={signaturePad?.isEmpty() || !canvasChanged}
            size="tiny"
            onPress={() => onApply()}
          >
            Übernehmen
          </Button>
          <Button
            className="flex ml-1 mb-1 shrink-0"
            variant={signaturePad?.isEmpty() ? "outline" : "primary"}
            isDisabled={signaturePad?.isEmpty() && !canvasChanged}
            size="tiny"
            onPress={() => onDiscard()}
          >
            {canvasChanged ? "Verwerfen" : "Leeren"}
          </Button>
        </div>
        <div className="flex btn-outline">
          <div className="canvas-container" ref={canvasContainerRef}>
            <canvas ref={canvasRef} id={[id, "canvas"].join("-")}
              className={"w-full h-full"} />
          </div>
        </div>
      </div>
      {error ? <p className="mt-1 text-xs text-red-500">{errorText}</p> : null}
    </div>
  );
};

export default SignField;
